import React, { useState, useEffect } from 'react';
import './Search.css';
import config from '../../config/config';
import { getRandomColors, setMedCSSVariables, resetMedCSSVariables } from '../disco/disco';

const API_LOG_SEARCH_URL = `${process.env.PUBLIC_URL}/api-docshark/log_searchbar_query`;

function Search({ update, initial, tour, dataType = 'main', searchText = false, totalRows = 0 }) {
  // Initialize query state with initial value or empty string
  const [query, setQuery] = useState(initial || '');
  // Initialize the disco state, defaulting to false
  const [disco, setDisco] = useState(false);

  // Function to send the search query to the logging endpoint
  const logSearchbarQuery = async (searchQuery) => {
    try {
      // Make the POST request to the logging endpoint
      await fetch(API_LOG_SEARCH_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ query: searchQuery }),
      });

      // No need to process the response or handle success explicitly for a fire-and-forget log

    } catch (error) {
      // Catch any errors but do not interrupt the main search flow or show anything to the user
      console.error("Failed to log searchbar query:", error);
    }
  };

  // Get the appropriate placeholder based on dataType
  const getPlaceholder = () => {
    return dataType === 'main'
      ? config.data.search_placeholder
      : config.data.secondary_search_placeholder;
  };

  // Handler that applies the search by updating the query
  const applySearch = () => {
    const searchQuery = query || '';  // Ensure query is never undefined

    // Ping the logging endpoint with the search query asynchronously
    // This call is fire-and-forget and won't affect the main search performance
    logSearchbarQuery(searchQuery);

    if (String(searchQuery).includes("medisee")) {
      // Apply disco effect if disco state is true
      toggleDisco();
    } else {
      update(new String(searchQuery)) // new String() is required here to also update with same query string
    }
  }

  // Handler that updates the search after pressing enter key
  const handlerKeyDown = (e) => {
    if (e.key === 'Enter') {
      applySearch()
    }
  }

  // Handler that clears the search query
  const clearSearch = () => {
    setQuery('')
    update('')
  }

  // Update query when initial prop changes
  useEffect(() => {
    setQuery(initial || '');
  }, [initial]);

  // Disco code
  useEffect(() => {
    // Check if the disco state is saved in localStorage
    const savedDisco = localStorage.getItem('disco');
    // If the savedDisco is not null, set the disco state accordingly
    if (savedDisco !== null) {
      setDisco(savedDisco === 'true');

      // Apply disco effect if savedDisco is true
      if (savedDisco === 'true') {
        const header = document.querySelector('.med-main-header');
        if (header) {
          header.classList.add('disco');
        }
      }
    }
  }, []);

  // Handler to toggle the disco state
  const toggleDisco = () => {
    const newDisco = !disco;
    setDisco(newDisco);
    // Save the updated disco state to localStorage
    localStorage.setItem('disco', newDisco.toString());

    // Apply or remove disco effect based on the new disco state
    const header = document.querySelector('.med-main-header');
    if (header) {
      if (newDisco) {
        header.classList.add('disco');
        setMedCSSVariables(getRandomColors());
      } else {
        header.classList.remove('disco');
        resetMedCSSVariables();
      }
    }
  };

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (disco) {
        setMedCSSVariables(getRandomColors());
      }
    }, 5000);

    return () => clearInterval(intervalId);
  }, [disco]);

  return (
    <div tour={tour} className={`med-content-container ${searchText ? 'med-search' : 'med-search-sticky'} med-home-content`}>
      {searchText && (
        <div>
          <h2 className="text-lg font-medium text-gray-900">Welcome to the European Medicines Regulatory Database (EMRD)</h2>
          <hr className="med-top-separator" />

          <div className="px-4">
            <p>
              <span>💡 <strong>Tip:</strong></span> Select {config.data.plural} using checkboxes on the left. Click the top-left checkbox to select all visible entries.
            </p>
            <p>
              <span>💡 <strong>Tip:</strong></span> Customize columns using the dropdowns and sort data with (▼).
            </p>
          </div>
          <hr className="med-top-separator" />
        </div>
      )}
      <div className="med-search-container">
        {query && (
          <i
            className="bx bx-x med-search-close-icon"
            onClick={clearSearch}
            data-testid="search-close-icon"
          ></i>
        )}
        <input
          type="text"
          id="med-search-input"
          placeholder={getPlaceholder()}
          className="med-search-text-input med-text-input"
          onChange={(e) => setQuery(e.target.value || '')}
          value={query}
          onKeyDown={handlerKeyDown}
        />
      </div>
      <button
        className="med-primary-solid med-bx-button med-search-button"
        onClick={applySearch}
      >
        <i className="bx bx-search med-button-image"></i>{config.data.search_button_text}
      </button>
    </div>
  )
}

export default Search
